import { useState, useContext } from 'react'
import {
  Typography,
  IconButton,
  Divider,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Grid,
} from '@mui/material'
import { ArrowForward, EditOutlined } from '@mui/icons-material'
import axios from 'axios'
import { userTypesList } from '../lists'
import { salesRepType, userType } from '../types'
import { dataContext } from '../index'
import './UserDetailsDrawer.css'
import { getRepCodeName } from '../helper'

interface UserDetailsDrawerProps {
  selectedUser: userType | null
  onUserUpdated: (updatedUser: userType) => void
  onError: (error: { show: boolean; displayName: string }) => void
  onClose: () => void
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="user-details-info-row">
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      <Typography variant="body2">{value ?? '-'}</Typography>
    </div>
  )
}

function UserDetailsDrawer({ selectedUser, onUserUpdated, onError, onClose }: UserDetailsDrawerProps) {
  const [editUserMode, setEditUserMode] = useState(false)
  const [editingUser, setEditingUser] = useState({
    displayName: '',
    userType: '',
    email: '',
  })

  const { salesReps } = useContext(dataContext)

  const getFilteredSalesReps = (): salesRepType[] => {
    if (!selectedUser) return []
    return salesReps
      .filter(rep => [rep.isr_email, rep.ae_email, rep.engineer_email].includes(selectedUser.email))
      .map(rep => ({
        initial: rep.initial,
        inside_sales_rep: rep.inside_sales_rep,
        account_executive: rep.account_executive,
        engineer: rep.engineer,
        isr_email: rep.isr_email,
        ae_email: rep.ae_email,
        engineer_email: rep.engineer_email,
        active: Boolean(rep.active),
        phone: rep.phone,
      }))
  }

  const handleEditUser = async () => {
    if (!selectedUser) return

    // Map new frontend role names to old backend role names/IDs
    let backendUserType = editingUser.userType
    if (editingUser.userType === 'Operations') {
      backendUserType = 'Operation Staff'
    } else if (editingUser.userType === 'Sales') {
      backendUserType = 'Sales Rep'
    } else if (editingUser.userType === 'Read Only') {
      backendUserType = 'Office Staff'
    }

    const requestData = {
      display_name: editingUser.displayName,
      user_type: backendUserType,
      email: editingUser.email,
    }

    try {
      await axios.put(`/user/${selectedUser.user_id}`, requestData)

      // Create the updated user object
      const updatedUser: userType = {
        ...selectedUser,
        display_name: editingUser.displayName,
        user_type: editingUser.userType,
        email: editingUser.email,
      }

      onUserUpdated(updatedUser)
      setEditUserMode(false)
      onError({ show: false, displayName: '' })
    } catch (error) {
      console.error('Error updating user:', error)
      onError({ show: true, displayName: editingUser.displayName })
    }
  }

  return (
    <div className="user-details-drawer">
      <div className="user-details-drawer-header">
        <div>
          <Typography variant="h6">User Details</Typography>
          <IconButton onClick={onClose}>
            <ArrowForward />
          </IconButton>
        </div>
        <Divider />
      </div>
      <div className="user-details-form-container">
        {!selectedUser ? (
          <div>No user selected</div>
        ) : editUserMode ? (
          <>
            <Grid container spacing="17px">
              <Grid size={6}>
                <TextField
                  label="Role"
                  value={editingUser.userType}
                  select
                  size="small"
                  fullWidth
                  className="role-field-full-width"
                  onChange={e => setEditingUser(prev => ({ ...prev, userType: e.target.value }))}
                >
                  {userTypesList.map(type => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Full Name"
                  value={editingUser.displayName}
                  onChange={e => setEditingUser(prev => ({ ...prev, displayName: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={editingUser.email}
                  onChange={e => setEditingUser(prev => ({ ...prev, email: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
            <div className="edit-user-buttons-container">
              <Button
                variant="outlined"
                onClick={() => {
                  setEditUserMode(false)
                  setEditingUser({
                    displayName: selectedUser.display_name ?? '',
                    userType: selectedUser.user_type,
                    email: selectedUser.email,
                  })
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleEditUser}
                disabled={!editingUser.userType || !editingUser.displayName || !editingUser.email}
              >
                Save Changes
              </Button>
            </div>
          </>
        ) : (
          <>
            <InfoRow label="User Type" value={selectedUser.user_type} />
            <InfoRow label="Full Name" value={selectedUser.display_name} />
            <InfoRow label="Email" value={selectedUser.email} />
            <div className="user-details-buttons-container">
              <Button
                variant="contained"
                startIcon={<EditOutlined />}
                onClick={() => {
                  const editingUserData = {
                    displayName: selectedUser.display_name ?? '',
                    userType: selectedUser.user_type,
                    email: selectedUser.email,
                  }

                  setEditUserMode(true)
                  setEditingUser(editingUserData)
                }}
              >
                Edit User Details
              </Button>
            </div>
          </>
        )}
      </div>
      {selectedUser && selectedUser.user_type === 'Sales' && (
        <Table className="sales-rep-table">
          <TableHead className="users-table-header">
            <TableRow>
              <TableCell>
                <Typography className="users-cell-header-text" variant="subtitle2" color="text.primary">
                  Sales Rep Code
                </Typography>
              </TableCell>
              <TableCell>
                <Typography className="users-cell-header-text" variant="subtitle2" color="text.primary">
                  Sales Rep Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography className="users-cell-header-text" variant="subtitle2" color="text.primary">
                  Sales Rep Active/Inactive
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredSalesReps().map(rep => (
              <TableRow key={rep.initial}>
                <TableCell>{rep.initial}</TableCell>
                <TableCell>{getRepCodeName(rep)}</TableCell>
                <TableCell>
                  <Chip
                    label={rep.active ? 'Active' : 'Inactive'}
                    size="small"
                    variant="outlined"
                    color={rep.active ? 'success' : 'error'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

export default UserDetailsDrawer
